/**
 * Form utilities for keyboard navigation and Enter-to-Tab behavior.
 * Handles Enter key interception and focus management within forms.
 */

'use strict';

import type { DotNetObjectReference } from '../types.js';

/**
 * Active form handlers tracked by form element reference.
 */
const formHandlers = new WeakMap<HTMLFormElement, {
    keydown: (e: KeyboardEvent) => void;
    keyup: (e: KeyboardEvent) => void;
}>();

/**
 * Gets all focusable elements within a container.
 */
function getFocusableElements(container: HTMLElement): HTMLElement[] {
    const selector = [
        'input:not([disabled]):not([type="hidden"])',
        'select:not([disabled])',
        'textarea:not([disabled])',
        'button:not([disabled])',
        'a[href]',
        '[tabindex]:not([tabindex="-1"])'
    ].join(', ');

    return Array.from(container.querySelectorAll<HTMLElement>(selector))
        .filter(el => {
            // Filter out hidden elements
            const style = window.getComputedStyle(el);
            return style.display !== 'none' && style.visibility !== 'hidden';
        });
}

/**
 * Checks if an element is a select component (native or custom).
 */
function isSelectElement(element: HTMLElement): boolean {
    // Native select elements
    if (element.tagName === 'SELECT') {
        return true;
    }

    // Custom select dropdown triggers
    if (
        element.tagName === 'BUTTON' &&
        element.hasAttribute('aria-expanded')
    ) {
        return true;
    }

    // Custom select components
    if (
        element.classList.contains('xa-select__dropdown-trigger') ||
        element.closest('.xa-select__dropdown-wrapper')
    ) {
        return true;
    }

    // Elements with dropdown/combobox roles
    if (
        element.getAttribute('role') === 'combobox' ||
        element.getAttribute('role') === 'listbox'
    ) {
        return true;
    }

    return false;
}

/**
 * Checks if an element is a dropdown trigger that should handle arrow keys.
 */
function isDropdownTrigger(element: HTMLElement): boolean {
    // Select dropdown triggers (button with aria-expanded)
    if (
        element.tagName === 'BUTTON' &&
        element.hasAttribute('aria-expanded')
    ) {
        return true;
    }

    // Custom select components
    if (
        element.classList.contains('xa-select__dropdown-trigger') ||
        element.closest('.xa-select__dropdown-wrapper')
    ) {
        return true;
    }

    // Elements with dropdown/combobox roles
    if (
        element.getAttribute('role') === 'combobox' ||
        element.getAttribute('role') === 'listbox'
    ) {
        return true;
    }

    return false;
}

/**
 * Checks if an element is an interactive component that should handle Enter naturally.
 */
function isInteractiveElement(element: HTMLElement): boolean {
    // Regular buttons (submit, button, reset) but NOT dropdown triggers
    if (element.tagName === 'BUTTON') {
        // If it's a dropdown trigger, it should NOT be considered interactive for Enter
        if (isDropdownTrigger(element)) {
            return false;
        }
        // Other buttons are interactive
        return true;
    }

    // Input buttons
    if (
        element.getAttribute('type') === 'submit' ||
        element.getAttribute('type') === 'button' ||
        element.getAttribute('type') === 'reset'
    ) {
        return true;
    }

    // Elements with button role (but not dropdown triggers)
    if (element.getAttribute('role') === 'button' && !isDropdownTrigger(element)) {
        return true;
    }

    // Elements explicitly marked to allow Enter
    if (element.hasAttribute('data-allow-enter')) {
        return true;
    }

    return false;
}

/**
 * Closes any open dropdown in the form.
 */
function closeOpenDropdowns(formElement: HTMLFormElement): void {
    // Find all dropdown triggers that are currently open
    const openDropdowns = formElement.querySelectorAll<HTMLElement>(
        'button[aria-expanded="true"].xa-select__dropdown-trigger'
    );
    
    openDropdowns.forEach(dropdown => {
        // Click to close the dropdown
        dropdown.click();
    });
}

/**
 * Handles arrow key navigation for dropdown triggers.
 */
function handleDropdownArrowKeys(element: HTMLElement, key: string): void {
    const ariaExpanded = element.getAttribute('aria-expanded');
    const isOpen = ariaExpanded === 'true';

    if (key === 'ArrowDown' && !isOpen) {
        // Open dropdown with Down arrow
        element.click();
    } else if (key === 'ArrowUp' && isOpen) {
        // Close dropdown with Up arrow
        element.click();
    }
}

/**
 * Initializes Enter-as-Tab behavior for a form.
 * When Enter is pressed on a non-button element, it will move focus to the next focusable element.
 * 
 * @param formElement - The form element reference
 * @param dotNetRef - .NET object reference (optional, for future callbacks)
 */
export function initializeEnterAsTab(
    formElement: HTMLFormElement,
    dotNetRef?: DotNetObjectReference
): void {
    if (!formElement) {
        console.warn('[Form] Invalid form element provided to initializeEnterAsTab');
        return;
    }

    // Clean up existing handler if present
    disposeEnterAsTab(formElement);

    const handleKeyDown = (e: KeyboardEvent): void => {
        const target = e.target as HTMLElement;

        // Allow arrow keys to work normally for all select elements (native and custom)
        // This includes navigating options in native selects and in custom dropdowns
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            if (isSelectElement(target)) {
                // Let the select element handle arrow keys naturally
                // Don't prevent default for native selects
                if (target.tagName === 'SELECT') {
                    return;
                }
                
                // For custom dropdowns, only open/close, don't prevent navigation inside
                if (isDropdownTrigger(target)) {
                    const ariaExpanded = target.getAttribute('aria-expanded');
                    const isOpen = ariaExpanded === 'true';
                    
                    // Only handle if dropdown is closed (to open it)
                    if (!isOpen && e.key === 'ArrowDown') {
                        e.preventDefault();
                        target.click();
                    } else if (isOpen && e.key === 'ArrowUp') {
                        e.preventDefault();
                        target.click();
                    }
                }
                return;
            }
        }

        // Handle Tab key to close dropdowns
        if (e.key === 'Tab') {
            // Close any open dropdowns when tabbing away
            closeOpenDropdowns(formElement);
            return;
        }

        // Only handle Enter key for Tab behavior
        if (e.key !== 'Enter') {
            return;
        }
        
        // Allow default behavior for interactive elements
        if (isInteractiveElement(target)) {
            return;
        }

        // Allow Shift+Enter in textareas for multi-line input
        if (target.tagName === 'TEXTAREA' && e.shiftKey) {
            return;
        }

        // Close any open dropdowns before moving focus
        closeOpenDropdowns(formElement);

        // Prevent default Enter behavior
        e.preventDefault();

        // Get all focusable elements
        const focusableElements = getFocusableElements(formElement);
        
        if (focusableElements.length === 0) {
            return;
        }

        // Find current index
        const currentIndex = focusableElements.indexOf(target);
        
        if (currentIndex === -1) {
            // If current element is not in the list, focus the first element
            focusableElements[0]?.focus();
            return;
        }

        // Move to next element (or wrap to first)
        const nextIndex = (currentIndex + 1) % focusableElements.length;
        focusableElements[nextIndex]?.focus();
    };

    const handleKeyUp = (e: KeyboardEvent): void => {
        // Additional keyup handling if needed in the future
    };

    // Attach the handlers
    formElement.addEventListener('keydown', handleKeyDown);
    formElement.addEventListener('keyup', handleKeyUp);
    
    // Store the handlers for cleanup
    formHandlers.set(formElement, {
        keydown: handleKeyDown,
        keyup: handleKeyUp
    });
}

/**
 * Disposes the Enter-as-Tab behavior for a form.
 * 
 * @param formElement - The form element reference
 */
export function disposeEnterAsTab(formElement: HTMLFormElement): void {
    if (!formElement) {
        return;
    }

    const handlers = formHandlers.get(formElement);
    if (handlers) {
        formElement.removeEventListener('keydown', handlers.keydown);
        formElement.removeEventListener('keyup', handlers.keyup);
        formHandlers.delete(formElement);
    }
}

/**
 * Focuses the first focusable element in the form.
 * 
 * @param formElement - The form element reference
 */
export function focusFirst(formElement: HTMLFormElement): void {
    if (!formElement) {
        return;
    }

    const focusableElements = getFocusableElements(formElement);
    if (focusableElements.length > 0) {
        focusableElements[0].focus();
    }
}

/**
 * Disposes all form handlers (cleanup utility).
 */
export function disposeAll(): void {
    // WeakMap doesn't support iteration, so individual disposal is required
    // This is kept as a no-op placeholder for API consistency
}
